import {
  createContext,
  useEffect,
  useContext,
  useReducer,
  useCallback,
  useRef,
} from 'react'
import apiService from '../services/ApiService'

export const AuthContext = createContext()
export const useAuth = () => useContext(AuthContext)

// Action types
const LOGIN_SUCCESS = 'LOGIN_SUCCESS'
const LOGOUT = 'LOGOUT'
const AUTH_LOADING = 'AUTH_LOADING'
const AUTH_ERROR = 'AUTH_ERROR'
const CLEAR_ERROR = 'CLEAR_ERROR'
const UPDATE_QUOTA = 'UPDATE_QUOTA'
const QUOTA_LOADING = 'QUOTA_LOADING'

const authReducer = (prevState, action) => {
  switch (action.type) {
    case LOGIN_SUCCESS:
      return {
        ...prevState,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }
    case LOGOUT:
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        quotaStatus: null,
        quotaLoading: false,
      }
    case AUTH_LOADING:
      return {
        ...prevState,
        isLoading: action.payload,
      }
    case AUTH_ERROR:
      return {
        ...prevState,
        error: action.payload,
        isLoading: false,
      }
    case CLEAR_ERROR:
      return {
        ...prevState,
        error: null,
      }
    case UPDATE_QUOTA:
      return {
        ...prevState,
        quotaStatus: action.payload,
        quotaLoading: false,
      }
    case QUOTA_LOADING:
      return {
        ...prevState,
        quotaLoading: action.payload,
      }
    default:
      return prevState
  }
}

const getInitialAuthState = () => {
  return {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    quotaStatus: null,
    quotaLoading: false,
  }
}

const normalizeLogoutOptions = (optionsOrRedirectFlag = false) => {
  if (typeof optionsOrRedirectFlag === 'boolean') {
    return {
      redirectToLogin: optionsOrRedirectFlag,
      preserveRedirectPath: false,
      skipBackendLogout: false,
    }
  }

  return {
    redirectToLogin: Boolean(optionsOrRedirectFlag?.redirectToLogin),
    preserveRedirectPath: Boolean(optionsOrRedirectFlag?.preserveRedirectPath),
    skipBackendLogout: Boolean(optionsOrRedirectFlag?.skipBackendLogout),
  }
}

const HEARTBEAT_DEFAULT_INTERVAL_MS = 60000
const HEARTBEAT_MIN_INTERVAL_MS = 15000
const HEARTBEAT_DEFAULT_JITTER_MS = 7000
const HEARTBEAT_DEFAULT_TIMEOUT_MS = 8000
const AUTH_SYNC_EVENT_KEY = 'gcctaxlaws_auth_sync_event'
const AUTH_SYNC_CHANNEL_NAME = 'gcctaxlaws-auth-sync'

const resolveHeartbeatIntervalMs = () => {
  const raw = Number(process.env.REACT_APP_AUTH_HEARTBEAT_INTERVAL_MS)
  if (!Number.isFinite(raw) || raw <= 0) {
    return HEARTBEAT_DEFAULT_INTERVAL_MS
  }
  return Math.max(HEARTBEAT_MIN_INTERVAL_MS, raw)
}

const resolveHeartbeatJitterMs = () => {
  const raw = Number(process.env.REACT_APP_AUTH_HEARTBEAT_JITTER_MS)
  if (!Number.isFinite(raw) || raw < 0) {
    return HEARTBEAT_DEFAULT_JITTER_MS
  }
  return Math.floor(raw)
}

const resolveHeartbeatTimeoutMs = () => {
  const raw = Number(process.env.REACT_APP_AUTH_HEARTBEAT_TIMEOUT_MS)
  if (!Number.isFinite(raw) || raw <= 0) {
    return HEARTBEAT_DEFAULT_TIMEOUT_MS
  }
  return Math.floor(raw)
}

const HEARTBEAT_INTERVAL_MS = resolveHeartbeatIntervalMs()
const HEARTBEAT_JITTER_MS = resolveHeartbeatJitterMs()
const HEARTBEAT_TIMEOUT_MS = resolveHeartbeatTimeoutMs()

export const AuthProvider = ({ children }) => {
  const [authState, dispatch] = useReducer(authReducer, getInitialAuthState())
  const quotaRefreshIntervalRef = useRef(null)
  const authStateRef = useRef(authState)
  const heartbeatTimeoutRef = useRef(null)
  const heartbeatInFlightRef = useRef(false)
  const authSyncChannelRef = useRef(null)
  const tabIdRef = useRef(
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  )

  useEffect(() => {
    authStateRef.current = authState
  }, [authState])

  const emitAuthSyncEvent = useCallback((eventType) => {
    const payload = {
      type: eventType,
      tabId: tabIdRef.current,
      at: Date.now(),
    }

    try {
      if (authSyncChannelRef.current) {
        authSyncChannelRef.current.postMessage(payload)
        return // if the broadcast channel is there and works, we can skip localStorage fallback
      }
    } catch (error) {
      console.warn('Failed to post auth sync event via BroadcastChannel', error)
    }

    try {
      localStorage.setItem(AUTH_SYNC_EVENT_KEY, JSON.stringify(payload))
      setTimeout(() => {
        localStorage.removeItem(AUTH_SYNC_EVENT_KEY)
      }, 0)
    } catch (error) {
      console.warn('Failed to write auth sync event to localStorage', error)
    }
  }, [])

  useEffect(() => {
    if (!authState.isAuthenticated) {
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current)
        heartbeatTimeoutRef.current = null
      }
      heartbeatInFlightRef.current = false
      return undefined
    }

    let disposed = false

    const clearHeartbeatTimer = () => {
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current)
        heartbeatTimeoutRef.current = null
      }
    }

    const scheduleNextHeartbeat = (baseDelayMs = HEARTBEAT_INTERVAL_MS) => {
      if (disposed) return
      clearHeartbeatTimer()
      const jitter = HEARTBEAT_JITTER_MS > 0
        ? Math.floor(Math.random() * (HEARTBEAT_JITTER_MS + 1))
        : 0
      const delayMs = Math.max(HEARTBEAT_MIN_INTERVAL_MS, baseDelayMs + jitter)
      heartbeatTimeoutRef.current = setTimeout(() => {
        void runHeartbeat('interval')
      }, delayMs)
    }

    const runHeartbeat = async (source = 'interval') => {
      if (disposed) return
      if (document.hidden) {
        scheduleNextHeartbeat()
        return
      }
      if (heartbeatInFlightRef.current) {
        scheduleNextHeartbeat(HEARTBEAT_MIN_INTERVAL_MS)
        return
      }

      heartbeatInFlightRef.current = true
      try {
        const result = await apiService.getSessionStatus({
          timeoutMs: HEARTBEAT_TIMEOUT_MS,
        })

        if (disposed) return

        if (!result?.isAuthenticated && result?.status === 401) {
          console.warn('Session heartbeat detected expired/invalid session, forcing logout')
          apiService.clearAuthAndRedirect()
          return
        }
      } catch (error) {
        if (!disposed) {
          console.warn(`Session heartbeat check failed (${source})`, error)
        }
      } finally {
        heartbeatInFlightRef.current = false
      }

      scheduleNextHeartbeat()
    }

    const handleVisibilityChange = () => {
      if (disposed) return
      if (document.hidden) {
        clearHeartbeatTimer()
        return
      }
      void runHeartbeat('visibility')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    scheduleNextHeartbeat()

    return () => {
      disposed = true
      clearHeartbeatTimer()
      heartbeatInFlightRef.current = false
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [authState.isAuthenticated])
  
  const refreshQuota = useCallback(async () => {
    try {
      const hasExistingQuota = Boolean(authStateRef.current?.quotaStatus)
      if (!hasExistingQuota) {
        dispatch({ type: QUOTA_LOADING, payload: true })
      }
      console.log('🔵 Fetching quota status...')

      const quotaData = await apiService.get(
        '/api/v1/quota/status',
        {},
        { bypassCache: true, skipCache: true }
      )

      console.log('✅ Quota fetched successfully:', quotaData)

      dispatch({
        type: UPDATE_QUOTA,
        payload: quotaData,
      })

      return quotaData
    } catch (error) {
      console.error('❌ Failed to refresh quota:', error)

      // If user is not authenticated, don't log as error
      if (error.response?.status === 401) {
        console.log('⚠️ User not authenticated - no quota available')
      }

      dispatch({ type: QUOTA_LOADING, payload: false })
      return null
    }
  }, [])

  const checkSession = useCallback(async (options = {}) => {
    const emitSyncEvent = Boolean(options?.emitSyncEvent)
    try {
      dispatch({ type: AUTH_LOADING, payload: true })
      const response = await apiService.get(
        '/api/v1/auth/oauth2/callback/success',
        {},
        { bypassCache: true, skipCache: true }
      )

      if (response && response.user) {
        const wasAuthenticated = Boolean(authStateRef.current?.isAuthenticated)
        dispatch({
          type: LOGIN_SUCCESS,
          payload: { user: response.user },
        })
        if (emitSyncEvent && !wasAuthenticated) {
          emitAuthSyncEvent('login')
        }
console.log('✅ User authenticated, fetching quota...')
        // Fetch quota status after successful login
        await refreshQuota()
        return response.user
      } else {
        dispatch({ type: LOGOUT })
        return null
      }
    } catch (error) {
      console.error('Session check failed:', error)
      // If session expired (401) or not found (400), properly logout
      if (error.response?.status === 401 || error.response?.status === 400) {
        // Session expired or invalid - clear local state but don't redirect
        // (let the user stay on current page, ApiService will handle redirect on next API call)
        apiService.clearCache()
        dispatch({ type: LOGOUT })
      } else {
        // Other errors - just clear state
        dispatch({ type: LOGOUT })
      }
      return null
    } finally {
      dispatch({ type: AUTH_LOADING, payload: false })
    }
  }, [emitAuthSyncEvent, refreshQuota])

  useEffect(() => {
    const handleRemoteAuthEvent = async (eventPayload) => {
      if (!eventPayload || eventPayload.tabId === tabIdRef.current) {
        return
      }

      if (eventPayload.type === 'login') {
        await checkSession({ emitSyncEvent: false })
        return
      }

      if (eventPayload.type === 'logout') {
        apiService.clearCache()
        dispatch({ type: LOGOUT })
      }
    }

    const handleStorage = (event) => {
      if (event.key !== AUTH_SYNC_EVENT_KEY || !event.newValue) {
        return
      }

      try {
        const payload = JSON.parse(event.newValue)
        void handleRemoteAuthEvent(payload)
      } catch (error) {
        console.warn('Failed to parse auth sync storage payload', error)
      }
    }

    if (typeof window === 'undefined') {
      return undefined
    }

    const canUseBroadcastChannel =
      typeof window.BroadcastChannel !== 'undefined'

    if (canUseBroadcastChannel) {
      try {
        const channel = new window.BroadcastChannel(AUTH_SYNC_CHANNEL_NAME)
        authSyncChannelRef.current = channel
        channel.onmessage = (event) => {
          void handleRemoteAuthEvent(event.data)
        }
      } catch (error) {
        console.warn('Failed to initialize auth sync BroadcastChannel', error)
        authSyncChannelRef.current = null
      }
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('storage', handleStorage)
      if (authSyncChannelRef.current) {
        authSyncChannelRef.current.close()
        authSyncChannelRef.current = null
      }
    }
  }, [checkSession])


  // Deduct quota (called after successful document access)
  const deductQuota = useCallback(
    (usageType, amount = 1) => {
      if (!authState.quotaStatus) return

      // Optimistic update
      const updatedQuota = { ...authState.quotaStatus }

      if (usageType === 'LINK_CLICK') {
        // Check priority: Plan > Free Quota > Credits
        if (updatedQuota.hasActiveSubscription) {
          // No deduction needed
        } else if (updatedQuota.remainingLinkClicks > 0) {
          updatedQuota.remainingLinkClicks -= amount
          updatedQuota.monthlyLinkClicksUsed += amount
        } else if (updatedQuota.creditsBalance > 0) {
          updatedQuota.creditsBalance -= amount
          updatedQuota.totalCreditsUsed += amount
        }
      } else if (usageType === 'DTAA_VIEW') {
        if (updatedQuota.hasActiveSubscription && updatedQuota.canAccessDtaa) {
          // No deduction needed
        } else if (updatedQuota.remainingDtaaViews > 0) {
          updatedQuota.remainingDtaaViews -= amount
          updatedQuota.monthlyDtaaViewsUsed += amount
        } else if (updatedQuota.creditsBalance > 0) {
          updatedQuota.creditsBalance -= amount
          updatedQuota.totalCreditsUsed += amount
        }
      }

      dispatch({
        type: UPDATE_QUOTA,
        payload: updatedQuota,
      })

      // Schedule actual refresh after a short delay
      setTimeout(() => refreshQuota(), 1000)
    },
    [authState.quotaStatus, refreshQuota]
  )

  const loginSuccess = useCallback(
    async (user) => {
      dispatch({
        type: LOGIN_SUCCESS,
        payload: { user },
      })

      // Fetch quota after login
      await refreshQuota()
      emitAuthSyncEvent('login')
    },
    [emitAuthSyncEvent, refreshQuota]
  )

  const logout = useCallback(async (optionsOrRedirectFlag = false) => {
    const { redirectToLogin, preserveRedirectPath, skipBackendLogout } =
      normalizeLogoutOptions(optionsOrRedirectFlag)

    try {
      dispatch({ type: AUTH_LOADING, payload: true })

      // Clear quota refresh interval
      if (quotaRefreshIntervalRef.current) {
        clearInterval(quotaRefreshIntervalRef.current)
      }

      if (!skipBackendLogout) {
        // Try to logout on backend, but don't fail if session is already expired
        await apiService.post('/api/v1/auth/oauth2/logout').catch((error) => {
          // If session is already expired (401) or doesn't exist (400), that's fine
          // We still want to clear local state
          if (error.response?.status === 401 || error.response?.status === 400) {
            console.log('Session already expired or invalid, clearing local state')
          } else {
            console.warn('Logout request failed:', error)
          }
        })
      }

      apiService.clearCache()
      if (!preserveRedirectPath) {
        sessionStorage.removeItem('redirectPath')
      }
      dispatch({ type: LOGOUT })
      emitAuthSyncEvent('logout')

      if (redirectToLogin && window.location.pathname !== '/login') {
        window.location.assign('/login')
      }
    } catch (error) {
      console.error('Logout error:', error)
      // Even if logout fails, clear local state
      apiService.clearCache()
      if (!preserveRedirectPath) {
        sessionStorage.removeItem('redirectPath')
      }
      dispatch({ type: LOGOUT })
      emitAuthSyncEvent('logout')

      if (redirectToLogin && window.location.pathname !== '/login') {
        window.location.assign('/login')
      }
    } finally {
      dispatch({ type: AUTH_LOADING, payload: false })
    }
  }, [emitAuthSyncEvent])

  // Check session on app load
  useEffect(() => {
    void checkSession()
  }, [checkSession])

  // Set auth context reference in ApiService
  useEffect(() => {
    apiService.setAuthContext({
      logout,
      refreshQuota, // expose to ApiService
    })
  }, [logout, refreshQuota])

  // Setup quota refresh on visibility change
  useEffect(() => {
    if (authState.isAuthenticated) {
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          // Page became visible - refresh quota
          void refreshQuota()
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
    return undefined
  }, [authState.isAuthenticated, refreshQuota])

  const hasRole = useCallback(
    (roleName) => {
      if (!authState.user?.roles) return false
      const roles = authState.user.roles
        .split(',')
        .map((role) => role.replace('ROLE_', ''))
      return roles.includes(roleName)
    },
    [authState.user]
  )

  const hasAnyRole = useCallback(
    (roleNames) => {
      if (!authState.user?.roles) return false
      const roles = authState.user.roles
        .split(',')
        .map((role) => role.replace('ROLE_', ''))
      return roleNames.some((role) => roles.includes(role))
    },
    [authState.user]
  )

  const isAdmin = useCallback(() => hasRole('ADMIN'), [hasRole])

  const clearError = useCallback(() => {
    dispatch({ type: CLEAR_ERROR })
  }, [])

  // Helper to check if quota is low
  const isQuotaLow = useCallback(() => {
    if (!authState.quotaStatus) return false

    const {
      hasActiveSubscription,
      remainingLinkClicks,
      remainingDtaaViews,
      monthlyLinkClicksLimit,
      monthlyDtaaViewsLimit,
    } = authState.quotaStatus

    // If has active subscription with unlimited access, quota is not low
    if (hasActiveSubscription) return false

    // Check if any quota is critically low
    const linkQuotaLow = remainingLinkClicks < monthlyLinkClicksLimit / 2 // FREE_LINK_QUOTA = 20
    const dtaaQuotaLow = remainingDtaaViews < monthlyDtaaViewsLimit // FREE_DTAA_QUOTA = 1
    return linkQuotaLow || dtaaQuotaLow // || creditsLow
  }, [authState.quotaStatus])

  const contextValue = {
    // State
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    error: authState.error,
    quotaStatus: authState.quotaStatus,
    quotaLoading: authState.quotaLoading,

    // Functions
    hasRole,
    hasAnyRole,
    isAdmin,
    loginSuccess,
    logout,
    clearError,
    checkSession,

    // quota functions
    refreshQuota,
    deductQuota,
    isQuotaLow,
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export default AuthProvider
